import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from '@/utils/utils';

// A filter pill and its menu, as production draws them; SortSelect shares them
/** The pill: clear on the page, its line kept when hovered, focused or open, a grey wash instead, its caret flipping as it opens. */
export const FILTER_TRIGGER =
  'case-lureo-label-case font-style-lureo-label-style text-shadow-lureo-label-shadow tracking-lureo-label-tracking bg-transparent hover:bg-lureo-neutral-strong focus:bg-lureo-neutral-strong hover:border-lureo-line-strong focus:border-lureo-line-strong focus-visible:border-lureo-line-strong data-[state=open]:border-lureo-line-strong [&[data-state=open]>svg]:rotate-180';
/** The menu: grey, rounder than a list menu, with a line round it. */
export const FILTER_MENU = '-mt-1 rounded-lureo-radius-xl border-lureo-border-width border-lureo-line bg-lureo-neutral px-1 pt-3 pb-2 text-lureo-ink-strong';
/** A row: a full pill, in small text, washed a darker grey when highlighted or chosen, its text the strong ink throughout (not a list menu's row text). */
export const FILTER_ITEM =
  'mx-1 w-auto rounded-lureo-radius-pill pr-2 text-lureo-12 leading-4 text-lureo-ink-strong hover:bg-lureo-neutral-strong hover:text-lureo-ink-strong focus:bg-lureo-neutral-strong focus:text-lureo-ink-strong data-[highlighted]:bg-lureo-neutral-strong data-[highlighted]:text-lureo-ink-strong';

interface FilterOption {
  value: string;
  label: string;
  onClick?: () => void;
  preventSelection?: boolean;
}

interface GenericFilterProps<T extends string> {
  options: FilterOption[];
  onFilterChange: (value: T) => void;
  placeholder?: string;
  defaultValue?: T;
  value?: T;
  borderColor?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

function Filter<T extends string>({
  options,
  onFilterChange,
  placeholder = "Select an option",
  defaultValue,
  value: controlledValue,
  className,
  onClick
}: GenericFilterProps<T>) {
  const [ownValue, setOwnValue] = React.useState(defaultValue);
  const value = controlledValue ?? ownValue;

  const handleValueChange = (newValue: string) => {
    // Find the option with this value
    const selectedOption = options.find(opt => opt.value === newValue);

    // Call the option's onClick handler if it exists
    if (selectedOption?.onClick) {
      console.log("Calling onClick for:", selectedOption.label);
      selectedOption.onClick();
    }

    // Only update value and call onFilterChange if preventSelection is not true
    if (!selectedOption?.preventSelection) {
      setOwnValue(newValue as T);
      onFilterChange(newValue as T);
    }
  };

  return (
    <Select
      onValueChange={handleValueChange}
      defaultValue={defaultValue}
      value={value}
    >
      {/* Its caret follows its words, as production's does */}
      <SelectTrigger
        size="xs"
        className={cn("h-9 w-32 justify-start rounded-lureo-radius-pill ps-4 pe-3 whitespace-nowrap", FILTER_TRIGGER, className)}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
      >
        <SelectValue placeholder={placeholder} className="text-left truncate flex-1 min-w-0" />
      </SelectTrigger>
      <SelectContent className={cn("w-full", FILTER_MENU)} sideOffset={4}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} className={FILTER_ITEM}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default Filter;